import React from 'react';
import { useParams } from 'react-router-dom';
import { ArrowLeftIcon, PrinterIcon } from '@heroicons/react/24/outline';
import { ScopedLink } from '../../components/ScopedLink';
import { useGetStaffComplianceQuery } from '../../store/api/localApi';
import { staffDisplayName } from './staff-display-name';
import { CredentialType, VettingStatus } from '../../types';
import './StaffCompliancePrint.print.css';

/**
 * /staff/:staffUserId/compliance/print — the inspection artefact for one
 * staff member's Garda vetting, credentials, and reference checks.
 *
 * REPLACES PDF GENERATION (see task-18-brief CONTROLLER ADDENDUM item 1):
 * there is no PDF library anywhere in this repo, and `react-pdf` in
 * package.json is Mozilla's pdf.js VIEWER, which renders existing PDFs and
 * cannot produce one. So the artefact is this print-styled page — the admin
 * opens it and uses the browser's own "Save as PDF" / print dialog, which
 * works on every device and adds zero dependencies. Same pattern as
 * IncidentDetail.print.css and MedicationPrintView.
 *
 * WHAT MUST BE ON THE PAGE (addendum item 2), because it is what an
 * inspector reads:
 *   - the person's name
 *   - every vetting record, append-only history, NEWEST FIRST — not just the
 *     current one (same ordering StaffComplianceProfile.tsx uses)
 *   - every credential with award/expiry dates and verified state
 *   - every reference check
 *   - a generated-on date, so a printout's age is visible
 *
 * NO REGULATION NUMBERS (addendum item 3). If anything is cited at all it is
 * the Act by name — "National Vetting Bureau (Children and Vulnerable
 * Persons) Acts 2012-2016" — never an S.I. number: nothing in the code
 * branches on one, it could not be verified, and this page is the single
 * most likely place a wrong citation would be read by a real inspector.
 *
 * Access control is the SERVER's, via GET /staff/{staffUserId}/compliance —
 * admin can print anyone's record, a staff member can print their own (the
 * same requireStaffComplianceOwner rule StaffComplianceProfile.tsx relies
 * on). This page hides nothing on its own and is not relied on to.
 */

const VETTING_LABEL: Record<VettingStatus, string> = {
  [VettingStatus.NOT_RECORDED]: 'Not recorded',
  [VettingStatus.IN_PROGRESS]: 'In progress',
  [VettingStatus.VETTED]: 'Vetted',
  [VettingStatus.REVET_DUE]: 'Re-vet due',
};

const CREDENTIAL_TYPE_LABEL: Record<string, string> = {
  [CredentialType.FIRST_AID]: 'First Aid',
  [CredentialType.CHILD_PROTECTION]: 'Child Protection',
  [CredentialType.QUALIFICATION]: 'Qualification',
  [CredentialType.FIRE_SAFETY]: 'Fire Safety',
  [CredentialType.MANUAL_HANDLING]: 'Manual Handling',
  [CredentialType.OTHER]: 'Other',
};

function credentialTypeLabel(type: string): string {
  return CREDENTIAL_TYPE_LABEL[type] ?? type;
}

const StaffCompliancePrint: React.FC = () => {
  const { staffUserId } = useParams<{ staffUserId: string }>();
  const {
    data: profile,
    isLoading,
    isError,
  } = useGetStaffComplianceQuery(staffUserId ?? '', {
    skip: !staffUserId,
  });

  if (!staffUserId) {
    return (
      <div className="min-h-screen flex items-center justify-center text-sm text-slate-500">
        Invalid URL
      </div>
    );
  }

  if (isLoading) {
    return (
      <div
        className="min-h-[40vh] flex items-center justify-center text-sm text-gray-500"
        data-testid="staff-compliance-print-loading"
      >
        Loading compliance record…
      </div>
    );
  }

  // Same rule as StaffComplianceProfile.tsx: the server is the real gate
  // (GET /staff/{staffUserId}/compliance refuses a colleague's record with a
  // 403), but this page must not leave a refused request rendering the
  // print SHELL — `staff-compliance-print` testid and all — for someone the
  // server just said no to. Distinct testid so a caller can assert this
  // branch positively rather than relying on the loaded testid's absence,
  // which is also 0 while merely loading.
  if (isError || !profile) {
    return (
      <div
        data-testid="staff-compliance-print-denied"
        className="min-h-screen flex items-center justify-center text-sm text-slate-500"
      >
        You don&apos;t have access to this page.
      </div>
    );
  }

  // Newest-first, append-only history — same ordering rule as
  // StaffComplianceProfile.tsx, and for the same reason: a re-vet appends
  // rather than replaces, so the current record is whichever is first here,
  // not "the only one".
  const vetting = [...(profile?.vetting ?? [])].sort((a, b) =>
    b.createdAt.localeCompare(a.createdAt)
  );
  const credentials = profile?.credentials ?? [];
  const references = profile?.references ?? [];
  const staffName = staffDisplayName(profile) || 'Compliance record';

  const generatedOn = new Date().toLocaleString('en-IE', {
    timeZone: 'Europe/Dublin',
    dateStyle: 'long',
    timeStyle: 'short',
  });

  return (
    <div
      data-testid="staff-compliance-print"
      className="max-w-4xl mx-auto px-4 py-8 sm:px-6 lg:px-8"
    >
      {/* Screen-only control bar — meaningless once printed. */}
      <div className="staff-compliance-print-only-hide flex items-center justify-between gap-3">
        <ScopedLink
          to={`/staff/${staffUserId}/compliance`}
          data-testid="staff-compliance-print-back"
          className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700"
        >
          <ArrowLeftIcon className="h-4 w-4" aria-hidden="true" />
          Back to profile
        </ScopedLink>
        <button
          type="button"
          data-testid="staff-compliance-print-button"
          onClick={() => window.print()}
          className="inline-flex items-center gap-1.5 rounded-md bg-gray-800 px-4 py-2 text-sm text-white hover:bg-gray-900"
        >
          <PrinterIcon className="h-4 w-4" aria-hidden="true" />
          Print
        </button>
      </div>

      {/* The record header — rendered on screen and on paper. Identifies
          whose record this is and when it was generated, so a printout's
          age is always visible. */}
      <header className="mt-4 border-b-2 border-gray-800 pb-3">
        <h1 className="text-xl font-bold text-gray-900">Staff compliance record</h1>
        <dl className="mt-1 text-sm text-gray-700 space-y-0.5">
          <div>
            <dt className="inline font-medium">Name: </dt>
            <dd className="inline" data-testid="staff-compliance-print-name">
              {staffName}
            </dd>
          </div>
          <div>
            <dt className="inline font-medium">Generated: </dt>
            <dd className="inline" data-testid="staff-compliance-print-generated-on">
              {generatedOn}
            </dd>
          </div>
        </dl>
        {/* No S.I. regulation number: nothing in the code branches on one and
            it could not be verified — see task-18-brief CONTROLLER ADDENDUM
            item 3. The Act is named, never a statutory instrument number. */}
        <p className="mt-2 text-xs text-gray-500">
          Garda vetting recorded under the National Vetting Bureau (Children and Vulnerable Persons)
          Acts 2012-2016.
        </p>
      </header>

      {/* ---- Vetting ---- */}
      <section
        data-testid="print-vetting-section"
        className="staff-compliance-print-section mt-6 bg-white border border-gray-300 p-4"
      >
        <h2 className="text-base font-semibold text-gray-900">Garda vetting</h2>
        <p className="mt-0.5 text-xs text-gray-600">
          Current status: {VETTING_LABEL[profile?.vettingStatus ?? VettingStatus.NOT_RECORDED]}
        </p>

        {vetting.length === 0 ? (
          <p className="mt-3 text-sm text-gray-600">Not recorded</p>
        ) : (
          <ul className="mt-3 divide-y divide-gray-200">
            {vetting.map((v) => (
              <li
                key={v.vettingId}
                data-testid={`print-vetting-row-${v.vettingId}`}
                className="staff-compliance-print-row py-2"
              >
                <div className="flex flex-wrap items-baseline justify-between gap-x-3">
                  <span className="text-sm font-semibold text-gray-900">
                    {VETTING_LABEL[v.vettingStatus]}
                  </span>
                  {v.disclosureDate && (
                    <span className="text-xs text-gray-600">Disclosure {v.disclosureDate}</span>
                  )}
                </div>
                <dl className="mt-1 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-0.5 text-xs text-gray-700">
                  <div>
                    <dt className="inline font-medium">NVB reference: </dt>
                    <dd className="inline">{v.nvbReference || 'Not recorded'}</dd>
                  </div>
                  {v.vettedThrough && (
                    <div>
                      <dt className="inline font-medium">Vetted through: </dt>
                      <dd className="inline">{v.vettedThrough}</dd>
                    </div>
                  )}
                  {v.decision && (
                    <div>
                      <dt className="inline font-medium">Decision: </dt>
                      <dd className="inline">{v.decision}</dd>
                    </div>
                  )}
                  {v.decidedByName && (
                    <div>
                      <dt className="inline font-medium">Decided by: </dt>
                      <dd className="inline">{v.decidedByName}</dd>
                    </div>
                  )}
                  {v.revetDueAt && (
                    <div>
                      <dt className="inline font-medium">Re-vet due: </dt>
                      <dd className="inline">{v.revetDueAt}</dd>
                    </div>
                  )}
                  <div>
                    <dt className="inline font-medium">Recorded by: </dt>
                    <dd className="inline">{v.createdByName}</dd>
                  </div>
                  <div>
                    <dt className="inline font-medium">Recorded on: </dt>
                    <dd className="inline">{v.createdAt.slice(0, 10)}</dd>
                  </div>
                </dl>
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* ---- Credentials ---- */}
      <section
        data-testid="print-credentials-section"
        className="staff-compliance-print-section mt-4 bg-white border border-gray-300 p-4"
      >
        <h2 className="text-base font-semibold text-gray-900">Credentials</h2>

        {credentials.length === 0 ? (
          <p className="mt-3 text-sm text-gray-600">Not recorded</p>
        ) : (
          <ul className="mt-3 divide-y divide-gray-200">
            {credentials.map((c) => (
              <li
                key={c.credentialId}
                data-testid={`print-credential-row-${c.credentialId}`}
                className="staff-compliance-print-row py-2"
              >
                <div className="flex flex-wrap items-baseline justify-between gap-x-3">
                  <span className="text-sm font-semibold text-gray-900">
                    {c.title}{' '}
                    <span className="font-normal text-gray-600">
                      ({credentialTypeLabel(c.credentialType)})
                    </span>
                  </span>
                  <span className="text-xs font-medium text-gray-700">
                    {c.verified ? 'Verified' : 'Not yet verified'}
                  </span>
                </div>
                <dl className="mt-1 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-0.5 text-xs text-gray-700">
                  <div>
                    <dt className="inline font-medium">Issuer: </dt>
                    <dd className="inline">{c.issuer || 'Not recorded'}</dd>
                  </div>
                  <div>
                    <dt className="inline font-medium">Awarded: </dt>
                    <dd className="inline">{c.awardedDate || 'Not recorded'}</dd>
                  </div>
                  <div>
                    <dt className="inline font-medium">Expires: </dt>
                    <dd className="inline">{c.expiryDate || 'Not recorded'}</dd>
                  </div>
                  {c.verified && c.verifiedByName && (
                    <div>
                      <dt className="inline font-medium">Verified by: </dt>
                      <dd className="inline">{c.verifiedByName}</dd>
                    </div>
                  )}
                </dl>
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* ---- References ---- */}
      <section
        data-testid="print-references-section"
        className="staff-compliance-print-section mt-4 bg-white border border-gray-300 p-4"
      >
        <h2 className="text-base font-semibold text-gray-900">References</h2>

        {references.length === 0 ? (
          <p className="mt-3 text-sm text-gray-600">Not recorded</p>
        ) : (
          <ul className="mt-3 divide-y divide-gray-200">
            {references.map((r) => (
              <li
                key={r.referenceId}
                data-testid={`print-reference-row-${r.referenceId}`}
                className="staff-compliance-print-row py-2"
              >
                <div className="flex flex-wrap items-baseline justify-between gap-x-3">
                  <span className="text-sm font-semibold text-gray-900">{r.refereeName}</span>
                  <span className="text-xs text-gray-600">
                    Validated {r.validatedAt.slice(0, 10)}
                  </span>
                </div>
                <dl className="mt-1 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-0.5 text-xs text-gray-700">
                  <div>
                    <dt className="inline font-medium">Organisation: </dt>
                    <dd className="inline">{r.refereeOrganisation || 'Not recorded'}</dd>
                  </div>
                  <div>
                    <dt className="inline font-medium">Validated by: </dt>
                    <dd className="inline">{r.validatedByName}</dd>
                  </div>
                  {r.note && (
                    <div className="sm:col-span-2">
                      <dt className="inline font-medium">Note: </dt>
                      <dd className="inline">{r.note}</dd>
                    </div>
                  )}
                </dl>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
};

export default StaffCompliancePrint;
